import React, { useState, useEffect, useMemo, useRef } from 'react'
import { v4 as uuidv4 } from 'uuid'
import {
    Modal,
    Stack,
    Typography,
    Breadcrumbs,
    IconButton,
    TextField,
    Grid2,
    Paper,
    Button,
    Link,
    CircularProgress,
    Alert,
    Snackbar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    TablePagination,
    ThemeProvider,
    Fade,
    Box,
    ClickAwayListener,
    Chip,
} from '@mui/material'
import {
    ArrowBack as ArrowBackIcon,
    Search as SearchIcon,
    Folder as FolderIcon,
    InsertDriveFile as FileIcon,
    Article as ArticleIcon,
    Description as WordIcon,
    TableChart as SpreadsheetIcon,
    PictureAsPdf as PdfIcon,
    Upload as UploadIcon,
    CreateNewFolder as CreateNewFolderIcon,
} from '@mui/icons-material'
import PDFWebViewComponent from '../../../components/PDFWebViewComponent'
import '../../../App.css'
import { useDemo } from '../../../contexts/DemoContext'
import { uploadMultipleFilesUpdated, listAllFiles, createFolder } from '../../../utils/utils'
import { ArtosTheme } from '../../../components/Themes'
import SingleSelectChip from '../../../components/SingleSelectChip'

import { documentTypes, getDocumentType } from '../../../utils/documentTypes'

// Cache configuration - same as SourceDocumentsPage
const CACHE_TTL = 5 * 60 * 1000 // 5 minutes in milliseconds
const CACHE_KEY_PREFIX = 'artos_file_cache_'

// Cache helper functions - same as SourceDocumentsPage for consistency
const cacheHelpers = {
    getCacheKey: (orgId, folderPath) => {
        return `${CACHE_KEY_PREFIX}${orgId}_${folderPath}`
    },
    
    getFromCache: (orgId, folderPath) => {
        try {
            const cacheKey = cacheHelpers.getCacheKey(orgId, folderPath)
            const cached = localStorage.getItem(cacheKey)
            
            if (!cached) return null
            
            const parsedCache = JSON.parse(cached)
            const now = Date.now()
            
            // Check if cache is expired
            if (now - parsedCache.timestamp > CACHE_TTL) {
                localStorage.removeItem(cacheKey)
                return null
            }
            
            return parsedCache.data
        } catch (error) {
            console.error('Error reading from cache:', error)
            return null
        }
    },
    
    saveToCache: (orgId, folderPath, data) => {
        try {
            const cacheKey = cacheHelpers.getCacheKey(orgId, folderPath)
            const cacheData = {
                data,
                timestamp: Date.now()
            }
            localStorage.setItem(cacheKey, JSON.stringify(cacheData))
        } catch (error) {
            console.error('Error saving to cache:', error)
            // If localStorage is full, clear old cache entries
            if (error.name === 'QuotaExceededError') {
                cacheHelpers.clearOldCacheEntries()
            }
        }
    },
    
    clearOldCacheEntries: () => {
        const now = Date.now()
        const keysToRemove = []
        
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            if (key && key.startsWith(CACHE_KEY_PREFIX)) {
                try {
                    const cached = JSON.parse(localStorage.getItem(key))
                    if (now - cached.timestamp > CACHE_TTL) {
                        keysToRemove.push(key)
                    }
                } catch (error) {
                    keysToRemove.push(key)
                }
            }
        }
        
        keysToRemove.forEach(key => localStorage.removeItem(key))
    },
    
    clearAllCache: () => {
        const keysToRemove = []
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i)
            if (key && key.startsWith(CACHE_KEY_PREFIX)) {
                keysToRemove.push(key)
            }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key))
    }
}

const getFileIcon = (fileName) => {
    const extension = fileName.split('.').pop().toLowerCase()

    switch (extension) {
        case 'doc':
        case 'docx':
            return <WordIcon />
        case 'csv':
        case 'xlsx':
        case 'xls':
            return <SpreadsheetIcon />
        case 'pdf':
            return <PdfIcon />
        case 'rtf':
            return <ArticleIcon />
        default:
            return <FileIcon />
    }
}

// const documentTypes = [
//     { name: 'Protocol', priority: 4, color: 'gray', textColor: 'white' },
//     { name: 'Standard Operating Procedure', priority: 3, color: 'gray', textColor: 'white' },
//     { name: 'Statistical Analysis Plan', priority: 2, color: 'gray', textColor: 'white' },
//     { name: 'Tables, Figures, and Lists', priority: 1, color: 'gray', textColor: 'white' },
//     { name: 'Unknown', priority: 5, color: 'red', textColor: 'white' },
// ]

const SourceSelectionModal = ({
    open,
    handleClose,
    onSourceSelect,
    isLoadingInputFiles: _isLoadingInputFiles,
    inputFiles: _inputFiles,
    setError,
    orgId,
    org_name,
    initialSelectedFiles = [],
    getOrgAccessToken,
    onFilesUploaded,
    isMultiSelect = true,
    allowedFolders = ['input', 'output', 'templates'],
    allowedSelectedFileTypes = []
}) => {
    const [selectedFiles, setSelectedFiles] = useState(initialSelectedFiles)
    const [showCreateFolder, setShowCreateFolder] = useState(false)
    const [newFolderName, setNewFolderName] = useState('')
    // const initialPath = useMemo(
    //     () => `/${orgId}/${allowedFolders[0]}`,
    //     [orgId, allowedFolders]
    // )
    const [currentPath, setCurrentPath] = useState(`/${orgId}`)
    const [searchQuery, setSearchQuery] = useState('')
    const [previewFile, setPreviewFile] = useState(null)
    const [isUploading, setIsUploading] = useState(false)
    const [uploadSuccess, setUploadSuccess] = useState(null)
    const [uploadedFiles, setUploadedFiles] = useState([])
    const [isDragging, setIsDragging] = useState(false)
    const [isUploadMode, setIsUploadMode] = useState(false)
    const [isPreviewExpanded, setIsPreviewExpanded] = useState(false)
    const [inputFiles, setInputFiles] = useState([])
    const [isLoadingInputFiles, setIsLoadingInputFiles] = useState(true)
    const fileInputRef = useRef(null)
    const { isDemo } = useDemo()
    const [orderBy, setOrderBy] = useState('name')
    const [order, setOrder] = useState('asc')
    const [page, setPage] = useState(0)
    const [rowsPerPage, setRowsPerPage] = useState(50)
    const [rowIndex, setRowIndex] = useState(-1)
    const [columnIndex, setColumnIndex] = useState(-1)
    const [dropdownOpen, setDropdownOpen] = useState(false)

    const fetchFiles = async (forceRefresh = false) => {
        try {
            console.log('orgId', orgId)
            setIsLoadingInputFiles(true)
            
            const allFiles = []
            const foldersNeedingFetch = []
            
            // Check cache for each folder first
            for (const folder of allowedFolders) {
                if (!forceRefresh) {
                    const cachedData = cacheHelpers.getFromCache(orgId, folder)
                    if (cachedData) {
                        console.log(`Using cached data for folder: ${folder}`)
                        allFiles.push(...cachedData)
                        continue
                    }
                }
                foldersNeedingFetch.push(folder)
            }
            
            // Only fetch folders that aren't cached or if force refresh
            if (foldersNeedingFetch.length > 0) {
                const orgAccessToken = await getOrgAccessToken()
                console.log('orgAccessToken', orgAccessToken)
                console.log('Fetching folders from server:', foldersNeedingFetch)
                
                const filePromises = foldersNeedingFetch.map(async (folder) => {
                    const files = await listAllFiles(folder, orgAccessToken)
                    // Save to cache immediately after fetching
                    cacheHelpers.saveToCache(orgId, folder, files)
                    return files
                })
                
                const fileArrays = await Promise.all(filePromises)
                allFiles.push(...fileArrays.flat())
            }
            
            console.log('allFiles', allFiles)
            const resultWithRandomDocumentTypeAndId = allFiles.map((file) => ({
                ...file,
                // document_type: getDocumentType(file.name),
                id: uuidv4(),
            }))

            setInputFiles(resultWithRandomDocumentTypeAndId)
        } catch (error) {
            console.error('Error fetching files:', error)
            setError?.(true)
        } finally {
            setIsLoadingInputFiles(false)
        }
    }

    useEffect(() => {
        if (open) {
            fetchFiles()
            // Auto-open folder if only one allowed folder exists
            if (allowedFolders.length === 1) {
                setCurrentPath(`/${orgId}/${allowedFolders[0]}`)
            } else {
                // Reset to root if more than one folder
                setCurrentPath(`/${orgId}`)
            }
        }
    }, [open, allowedFolders.join(',')])

    useEffect(() => {
        setSelectedFiles(initialSelectedFiles)
    }, [initialSelectedFiles])

    const handleFileUpload = async (files, fileContents) => {
        try {
            setIsUploading(true)
            const orgAccessToken = await getOrgAccessToken()
            const currentFolder = displayPathParts.slice(1).join('/');

            await uploadMultipleFilesUpdated(
                setIsUploading,
                files.map((file, index) => ({
                    name: file.name,
                    content: fileContents[index],
                })),
                fileContents,
                (error) => {
                    console.error('Upload error:', error)
                    setUploadSuccess(
                        'Failed to upload files. Please try again.'
                    )
                },
                currentFolder,
                orgAccessToken
            )

            const newFileNames = files.map((f) => f.name)
            setUploadedFiles(newFileNames)

            // Clear cache for the affected folder (where files were uploaded)
            const affectedFolder = currentFolder.split('/')[0] || currentFolder
            if (affectedFolder && allowedFolders.includes(affectedFolder)) {
                const cacheKey = cacheHelpers.getCacheKey(orgId, affectedFolder)
                localStorage.removeItem(cacheKey)
                console.log(`Cleared cache for folder: ${affectedFolder}`)
            }

            // Force refresh to get updated files
            await fetchFiles(true)

            onFilesUploaded?.()

            setIsUploadMode(false)

            setUploadSuccess(
                `Successfully uploaded ${files.length} file${
                    files.length > 1 ? 's' : ''
                }`
            )

            setTimeout(() => {
                setUploadSuccess(null)
            }, 6000)
        } catch (error) {
            console.error('Upload failed:', error)
            setUploadSuccess('Failed to upload files. Please try again.')
        } finally {
            setIsUploading(false)
        }
    }

    const { files, folders } = useMemo(() => {
        const currentFiles = []
        const currentFolders = new Set()

        if (!inputFiles || !Array.isArray(inputFiles)) {
            console.log('No input files or invalid format')
            return { files: [], folders: [] }
        }

        const memoizedCurrentPath = currentPath
        const memoizedAllowedFolders = allowedFolders

        inputFiles.forEach((file) => {
            if (!file || !file.name) {
                console.log('Invalid file object:', file)
                return
            }

            const filePath = file.name

            const normalizedCurrentPath = memoizedCurrentPath.replace(/^\//, '')
            const normalizedFilePath = filePath.replace(/^\//, '')
            
            const fileFolder = normalizedFilePath.split('/')[1]
            if (!memoizedAllowedFolders.includes(fileFolder)) {
                return
            }

            if (normalizedFilePath.startsWith(normalizedCurrentPath)) {
                let relativePath = normalizedFilePath.slice(
                    normalizedCurrentPath.length
                )
                relativePath = relativePath.replace(/^\//, '')

                if (!relativePath) {
                    return
                }

                const parts = relativePath.split('/')

                if (parts.length > 1) {
                    const folderPath = normalizedCurrentPath.split('/')[1]
                    if (memoizedAllowedFolders.includes(folderPath) || memoizedAllowedFolders.includes(parts[0])) {
                        currentFolders.add(parts[0])
                    }
                } else {
                    currentFiles.push(file)
                }
            }
        })

        console.log("currentFiles: ", currentFiles)
        console.log("currentFolders: ", currentFolders)

        return {
            files: currentFiles,
            folders: Array.from(currentFolders),
        }
    }, [inputFiles, currentPath, allowedFolders])

    const filteredItems = useMemo(() => {
        console.log("In Filtered Items")
        let filteredFiles = files
        let filteredFolders = folders
        const memoizedSearchQuery = searchQuery
        
        console.log("allowedSelectedFileTypes", allowedSelectedFileTypes)
        console.log("allowedSelectedFileTypes.length", allowedSelectedFileTypes.length)

        if (allowedSelectedFileTypes.length > 0) {
            console.log("In Allowed Select File Types")
            filteredFiles = filteredFiles.filter((f) => {
                const fileExtension = f.name.split('.').pop().toLowerCase()
                console.log('fileExtension', fileExtension)
                return allowedSelectedFileTypes.includes(fileExtension)
            })
            console.log('select file types filteredFiles', filteredFiles)
        }

        if (!memoizedSearchQuery) {
            console.log("No Search Query")
            return { files: filteredFiles, folders: filteredFolders }
        }

        console.log("memoizedSearchQuery", memoizedSearchQuery)
        const newFilteredFiles = filteredFiles.filter((f) => {
            return f.name.toLowerCase().includes(memoizedSearchQuery.toLowerCase())
        })
        console.log("newFilteredFiles", newFilteredFiles)

        const newFilteredFolders = filteredFolders.filter((f) => {
            return f.toLowerCase().includes(memoizedSearchQuery.toLowerCase())
        })
        console.log("newFilteredFolders", newFilteredFolders)

        return {
            files: newFilteredFiles,
            folders: newFilteredFolders,
        }
    }, [files, folders, searchQuery])

    const handleFolderClick = (folderName) => {
        const newPath = `${currentPath}/${folderName}`
        const folder = newPath.split('/')[2]
        if (allowedFolders.includes(folder)) {
            setCurrentPath(newPath)
        }
    }

    const handleBreadcrumbClick = (index) => {
        console.log('handleBreadcrumbClick', index)
        const parts = currentPath.split('/')
        const newPath = parts.slice(0, index + 1).join('/')
        const folder = newPath.split('/')[2]
        // Block navigation to root if only one folder is allowed
        if (allowedFolders.length === 1 && index <= 1) {
            return // Don't allow navigation to root when only one folder is allowed
        }
        if (allowedFolders.includes(folder) || index <= 1) {
            setCurrentPath(newPath)
        }
    }

    const handleBackClick = () => {
        const parts = currentPath.split('/')
        if (parts.length > 2) {
            parts.pop()
            const newPath = parts.join('/')
            const folder = newPath.split('/')[2]
            // Block navigation to root if only one folder is allowed
            if (allowedFolders.length === 1 && parts.length <= 2) {
                return // Don't allow navigation to root when only one folder is allowed
            }
            if (allowedFolders.includes(folder) || parts.length <= 2) {
                setCurrentPath(newPath)
            }
        }
    }

    const handleSelectFile = (file) => {
        let newSelectedFiles
        if (isMultiSelect) {
            if (selectedFiles.some((f) => f.name === file.name)) {
                newSelectedFiles = selectedFiles.filter(
                    (f) => f.name !== file.name
                )
            } else {
                newSelectedFiles = [...selectedFiles, file]
            }
        } else {
            newSelectedFiles = selectedFiles.some((f) => f.name === file.name)
                ? []
                : [file]
        }
        console.log('newSelectedFiles', newSelectedFiles)
        setSelectedFiles(newSelectedFiles)
        onSourceSelect(newSelectedFiles)
    }

    const handlePreviewClick = (file) => {
        setPreviewFile(file)
        if (selectedFiles.some((f) => f.name === file.name)) {
            return
        } else {
            handleSelectFile(file)
        }
    }

    const confirmSelection = () => {
        handleClose()
    }

    const pathParts = currentPath.split('/').filter(Boolean)
    const displayPathParts = pathParts.map((part, index) =>
        index === 0 ? org_name || part : part
    )

    // Helper to create a new folder
    const handleCreateFolder = async () => {
        try {
            const orgToken = await getOrgAccessToken()
            const currentFolder = displayPathParts.slice(1).join('/');
            await createFolder(newFolderName, currentFolder, orgToken)
            
            // Clear cache for the affected folder
            const affectedFolder = currentFolder.split('/')[0] || currentFolder
            if (affectedFolder && allowedFolders.includes(affectedFolder)) {
                const cacheKey = cacheHelpers.getCacheKey(orgId, affectedFolder)
                localStorage.removeItem(cacheKey)
                console.log(`Cleared cache for folder: ${affectedFolder}`)
            }
            
            await fetchFiles(true) // force refresh file list
            setShowCreateFolder(false)
            setNewFolderName('')
        } catch (error) {
            console.error('Error creating folder:', error)
        }
    }

    const handleUploadClick = () => {
        setIsUploadMode(true)
        const newPath = displayPathParts.slice(1).join('/');
        setCurrentPath(`/${orgId}/${newPath}`);
    }

    const handleCancelUpload = () => {
        setIsUploadMode(false)
        setIsDragging(false)
    }

    const isNewlyUploadedFile = (fileName) => {
        return uploadedFiles.includes(fileName.split('/').pop())
    }

    const handleDragOver = (e) => {
        e.preventDefault()
        setIsDragging(true)
    }

    const handleDragLeave = (e) => {
        e.preventDefault()
        setIsDragging(false)
    }

    const handleDrop = async (e) => {
        e.preventDefault()
        setIsDragging(false)

        const files = Array.from(e.dataTransfer.files)
        const fileContents = await Promise.all(
            files.map(
                (file) =>
                    new Promise((resolve) => {
                        const reader = new FileReader()
                        reader.onload = (e) => resolve(e.target.result)
                        reader.readAsArrayBuffer(file)
                    })
            )
        )

        handleFileUpload(files, fileContents)
    }

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files)
        const fileContents = await Promise.all(
            files.map(
                (file) =>
                    new Promise((resolve) => {
                        const reader = new FileReader()
                        reader.onload = (e) => resolve(e.target.result)
                        reader.readAsArrayBuffer(file)
                    })
            )
        )

        handleFileUpload(files, fileContents)
        e.target.value = ''
    }

    const handleSort = (property) => {
        const isAsc = orderBy === property && order === 'asc'
        setOrder(isAsc ? 'desc' : 'asc')
        setOrderBy(property)
    }

    const sortFiles = (files) => {
        return [...files].sort((a, b) => {
            const aName = a.name.split('/').pop().toLowerCase()
            const bName = b.name.split('/').pop().toLowerCase()

            if (orderBy === 'name') {
                return order === 'asc'
                    ? aName.localeCompare(bName)
                    : bName.localeCompare(aName)
            }
            return 0
        })
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage)
    }

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const paginatedFiles = useMemo(() => {
        const startIndex = page * rowsPerPage
        console.log('filteredItems.files', filteredItems.files)
        return sortFiles(filteredItems.files).slice(
            startIndex,
            startIndex + rowsPerPage
        )
    }, [filteredItems.files, page, rowsPerPage, orderBy, order])

    const handleDocumentTypeChange = (index, columnName, event) => {
        const value = event.target.value
        const newInputFiles = [...folders, ...paginatedFiles]
        newInputFiles[index + folders.length] = {
            ...newInputFiles[index + folders.length],
            [columnName]: value,
        }
        setInputFiles(newInputFiles)
        handleDropdownExit()
        // setTimeout(() => {}, 4000)
    }

    const handleDropdownExit = () => {
        setRowIndex(-1)
        setColumnIndex(-1)
        setDropdownOpen(false)
    }

    const handleDropdownClick = (index) => {
        setRowIndex(index)
        setColumnIndex(2)
        handleDropdownOpen()
    }

    const handleDropdownOpen = () => {
        setDropdownOpen(true)
    }

    const handleDropdownClose = (e) => {
        e.stopPropagation()
        setDropdownOpen(false)
    }

    const renderContent = () => {
        if (isPreviewExpanded && previewFile) {
            return (
                <Paper sx={{ height: '100%', overflow: 'hidden' }}>
                    <PDFWebViewComponent initialFile={previewFile.url} filename={previewFile.name} />
                </Paper>
            )
        }

        return (
            <Grid2 container spacing={2} sx={{ height: '100%', flexWrap: 'nowrap', padding: 1}}>
                <Grid2
                    item
                    xs={8}
                    sx={{
                        height: '100%',
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    {isLoadingInputFiles || isUploading ? (
                        <Stack
                            alignItems="center"
                            justifyContent="center"
                            sx={{ height: '100%' }}
                        >
                            <CircularProgress />
                            <Typography sx={{ mt: 2 }}>
                                {isUploading
                                    ? 'Uploading files...'
                                    : 'Loading files...'}
                            </Typography>
                        </Stack>
                    ) : isUploadMode ? (
                        <Paper
                            sx={{
                                height: '100%',
                                border: '2px dashed #ccc',
                                borderRadius: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                bgcolor: isDragging
                                    ? 'rgba(25, 118, 210, 0.08)'
                                    : 'transparent',
                                cursor: 'pointer',
                                '&:hover': {
                                    bgcolor: 'rgba(0, 0, 0, 0.04)',
                                },
                            }}
                            onDragOver={handleDragOver}
                            onDragLeave={handleDragLeave}
                            onDrop={handleDrop}
                            onClick={() => fileInputRef.current?.click()}
                        >
                            <UploadIcon
                                sx={{
                                    fontSize: 48,
                                    color: 'text.secondary',
                                    mb: 2,
                                }}
                            />
                            <Typography
                                variant="h6"
                                color="text.secondary"
                                align="center"
                                sx={{ mb: 1 }}
                            >
                                Click to upload a file or drag and drop
                            </Typography>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                align="center"
                                sx={{
                                    textDecoration: 'underline',
                                }}
                            >
                                PDF, DOCX, XLSX, CSV, or RTF up to 100MB
                            </Typography>
                        </Paper>
                    ) : (
                        <Paper
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                overflow: 'hidden',
                            }}
                        >
                            <TableContainer sx={{ flex: 1, overflow: 'auto', width: '100%' }}>
                                <Table stickyHeader size="small" sx={{ width: '100%', tableLayout: 'fixed' }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell
                                                padding="checkbox"
                                                sx={{ width: '5%' }}
                                            ></TableCell>
                                            <TableCell padding="normal" sx={{ width: '70%' }}>
                                                <TableSortLabel
                                                    active={orderBy === 'name'}
                                                    direction={order}
                                                    onClick={() =>
                                                        handleSort('name')
                                                    }
                                                >
                                                    Name
                                                </TableSortLabel>
                                            </TableCell>
                                            {/* <TableCell padding="normal">
                                                <TableSortLabel
                                                    active={orderBy === 'type'}
                                                    direction={order}
                                                    onClick={() =>
                                                        handleSort('type')
                                                    }
                                                >
                                                    Type
                                                </TableSortLabel>
                                            </TableCell> */}
                                            <TableCell
                                                align="center"
                                                padding="normal"
                                                sx={{ width: '25%' }}
                                            >
                                                
                                            </TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredItems.folders.map((folder) => (
                                            <TableRow
                                                key={folder}
                                                hover
                                                onClick={() =>
                                                    handleFolderClick(folder)
                                                }
                                                sx={{
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <TableCell padding="checkbox">
                                                    <FolderIcon color="primary" />
                                                </TableCell>
                                                <TableCell padding="normal">
                                                    {folder}
                                                </TableCell>
                                                {/* <TableCell padding="normal"></TableCell> */}
                                                <TableCell padding="normal"></TableCell>
                                            </TableRow>
                                        ))}
                                        {paginatedFiles.map((file, index) => {

                                            let fileName = file.name

                                            if (file.name.startsWith(`${orgId}/${allowedFolders[0]}/`)) {
                                                fileName = file.name.replace(`${orgId}/${allowedFolders[0]}/`, '')
                                            } else if (file.name.startsWith(`${orgId}/${allowedFolders[1]}/`)) {
                                                fileName = file.name.replace(`${orgId}/${allowedFolders[1]}/`, '')
                                            } else if (file.name.startsWith(`${orgId}/${allowedFolders[2]}/`)) {
                                                fileName = file.name.replace(`${orgId}/${allowedFolders[2]}/`, '')
                                            }
                                            
                                            return (
                                                <TableRow
                                                    key={file.id}
                                                    hover
                                                    selected={selectedFiles.some(
                                                        (f) =>
                                                            f.name === file.name
                                                    )}
                                                    sx={{
                                                        cursor: 'pointer',
                                                        transition:
                                                            'all 0.2s ease',
                                                        border: isNewlyUploadedFile(
                                                            file.name
                                                        )
                                                            ? '1px solid #1976d2'
                                                            : 'none',
                                                        '& .MuiTableCell-root':
                                                            {
                                                                transition:
                                                                    'all 0.2s ease',
                                                            },
                                                    }}
                                                >
                                                    <TableCell padding="checkbox">
                                                        {getFileIcon(fileName)}
                                                    </TableCell>
                                                    <TableCell padding="normal">
                                                        {fileName.split('/').pop()}
                                                    </TableCell>
                                                    {/* <ClickAwayListener
                                                        onClickAway={handleDropdownExit}
                                                    >
                                                        <TableCell
                                                            padding="normal"
                                                            sx={{
                                                                cursor: 'pointer',
                                                            }}
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                handleDropdownClick(index)
                                                            }}
                                                        >
                                                            {rowIndex === index &&
                                                            columnIndex === 2 ? (
                                                                <SingleSelectChip
                                                                    key={file.id}
                                                                    options={
                                                                        documentTypes
                                                                    }
                                                                    selection={
                                                                        file.document_type
                                                                    }
                                                                    handleChange={(
                                                                        event
                                                                    ) => {
                                                                        handleDocumentTypeChange(
                                                                            index,
                                                                            'document_type',
                                                                            event
                                                                        )
                                                                    }}
                                                                    open={dropdownOpen}
                                                                    handleOpen={(
                                                                        e
                                                                    ) =>
                                                                        handleDropdownOpen(
                                                                            e
                                                                        )
                                                                    }
                                                                    handleClose={
                                                                        handleDropdownClose
                                                                    }
                                                                />
                                                            ) : (
                                                                <Chip
                                                                    label={
                                                                        file
                                                                            .document_type
                                                                            .name
                                                                    }
                                                                    sx={{
                                                                        bgcolor:
                                                                            file
                                                                                .document_type
                                                                                .color,
                                                                        color: file
                                                                            .document_type
                                                                            .textColor,
                                                                        cursor: 'pointer',
                                                                        '&:hover': {
                                                                            bgcolor:
                                                                                'rgba(0, 0, 0, 0.04)',
                                                                        },
                                                                        '&:active':
                                                                            {
                                                                                bgcolor:
                                                                                    'rgba(0, 0, 0, 0.04)',
                                                                            },
                                                                        '&:focus': {
                                                                            bgcolor:
                                                                                'rgba(0, 0, 0, 0.04)',
                                                                        },
                                                                    }}
                                                                />
                                                            )}
                                                        </TableCell>
                                                    </ClickAwayListener> */}
                                                    <TableCell
                                                        align="center"
                                                        padding="normal"
                                                    >
                                                        <Button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                handlePreviewClick(file)
                                                            }}
                                                            size="small"
                                                            sx={{ width: '50%' }}
                                                        >
                                                            Preview
                                                        </Button>
                                                        <Button
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                handleSelectFile(file)
                                                                setSearchQuery('')
                                                            }}
                                                            variant={
                                                                selectedFiles.some(
                                                                    (f) =>
                                                                        f.name ===
                                                                        file.name
                                                                )
                                                                    ? 'contained'
                                                                    : 'outlined'
                                                            }
                                                            size="small"
                                                            sx={{ width: '50%' }}
                                                        >
                                                            {selectedFiles.some(
                                                                (f) =>
                                                                    f.name ===
                                                                    file.name
                                                            )
                                                                ? 'Selected'
                                                                : 'Select'}
                                                        </Button>
                                                    </TableCell>
                                                </TableRow>
                                            )
                                        })}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                            <TablePagination
                                component="div"
                                count={filteredItems.files.length}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[25, 50, 100]}
                                sx={{
                                    borderTop:
                                        '1px solid rgba(224, 224, 224, 1)',
                                    flexShrink: 0,
                                }}
                            />
                        </Paper>
                    )}
                </Grid2>
                <Grid2
                    item
                    xs={4}
                    sx={{
                        height: '100%',
                        width: '50%',
                    }}
                >
                    <Paper
                        sx={{
                            width: '100%',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                        }}
                    >
                        {previewFile ? (
                            <PDFWebViewComponent
                                initialFile={previewFile.url}
                                filename={previewFile.name}
                            />
                        ) : (
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{
                                    p: 2,
                                    textAlign: 'center',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                Select a file to preview
                            </Typography>
                        )}
                    </Paper>
                </Grid2>
            </Grid2>
        )
    }

    return (
        <ThemeProvider theme={ArtosTheme}>
            <Modal
                open={open}
                onClose={handleClose}
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Paper
                    sx={{
                        width: '80vw',
                        height: '80vh',
                        p: 3,
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                    }}
                >
                    <Stack spacing={2} sx={{ flexShrink: 0 }}>
                        <Typography variant="h5">Document Library</Typography>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <IconButton
                                onClick={handleBackClick}
                                disabled={
                                    pathParts.length <= 1 ||
                                    (searchQuery !== '' && pathParts.length <= 2) ||
                                    (allowedFolders.length === 1 && pathParts.length === 2)
                                }
                            >
                                <ArrowBackIcon />
                            </IconButton>
                            <Breadcrumbs sx={{ flexGrow: 1 }}>
                                {displayPathParts.map((part, index) => (
                                    <Link
                                        key={index}
                                        component="button"
                                        color="inherit"
                                        underline={index === 0 && allowedFolders.length === 1 ? "none" : "hover"}
                                        sx={{
                                            cursor: index === 0 && allowedFolders.length === 1 ? "default" : "pointer",
                                        }}
                                        disabled={
                                            (searchQuery !== '' && index !== 1) ||
                                            (allowedFolders.length === 1 && index === 0 && pathParts.length === 2)
                                        }
                                        onClick={() =>
                                            handleBreadcrumbClick(index + 1)
                                        }
                                    >
                                        {part}
                                    </Link>
                                ))}
                            </Breadcrumbs>
                            {previewFile && (
                                <Button
                                    variant="outlined"
                                    onClick={() =>
                                        setIsPreviewExpanded(!isPreviewExpanded)
                                    }
                                    sx={{ mr: 1 }}
                                >
                                    {isPreviewExpanded
                                        ? 'Close Preview'
                                        : 'Expand Preview'}
                                </Button>
                            )}
                            {!isUploadMode ? (
                                <>
                                    <Button
                                        variant="contained"
                                        startIcon={<CreateNewFolderIcon />}
                                        onClick={() =>
                                            setShowCreateFolder(
                                                (prev) => !prev
                                            )
                                        }
                                    >
                                        Create Folder
                                    </Button>
                                    <Button
                                        variant="contained"
                                        startIcon={<UploadIcon />}
                                        onClick={handleUploadClick}
                                        disabled={isUploading || orgId === '73fcdbb8-39da-48f9-823a-6b864bf3e9a6'}
                                    >
                                        Upload Files
                                    </Button>
                                    
                                </>
                            ) : (
                                <Button
                                    variant="outlined"
                                    onClick={handleCancelUpload}
                                    disabled={isUploading}
                                >
                                    Cancel Upload
                                </Button>
                            )}
                        </Stack>
                        {showCreateFolder && (
                            <Stack direction="row" spacing={1} alignItems="center">
                                <TextField
                                    label="New Folder Name"
                                    value={newFolderName}
                                    onChange={(e) => setNewFolderName(e.target.value)}
                                    size="small"
                                />
                                <Button
                                    variant="contained"
                                    onClick={handleCreateFolder}
                                    disabled={!newFolderName.trim()}
                                >
                                    Create
                                </Button>
                            </Stack>
                        )}
                        {!isPreviewExpanded && !isUploadMode && (
                            <Fade in={true} unmountOnExit>
                                <TextField
                                    fullWidth
                                    placeholder="Search in current folder..."
                                    value={searchQuery}
                                    onChange={(e) =>
                                        setSearchQuery(e.target.value)
                                    }
                                    InputProps={{
                                        startAdornment: (
                                            <SearchIcon sx={{ mr: 1 }} />
                                        ),
                                    }}
                                    sx={{
                                        px: 1,
                                    }}
                                />
                            </Fade>
                        )}
                    </Stack>

                    <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                        {renderContent()}
                    </Box>

                    <Stack
                        direction="row"
                        spacing={2}
                        justifyContent="flex-end"
                        sx={{ mt: 2, flexShrink: 0 }}
                    >
                        <Button onClick={handleClose} variant="outlined">
                            Cancel
                        </Button>
                        <Button
                            onClick={confirmSelection}
                            variant="contained"
                            disabled={selectedFiles.length === 0}
                        >
                            Confirm Selection ({selectedFiles.length})
                        </Button>
                    </Stack>

                    <input
                        type="file"
                        multiple
                        accept=".pdf,.docx,.xlsx,.csv,.rtf"
                        ref={fileInputRef}
                        style={{ display: 'none' }}
                        onChange={handleFileSelect}
                    />

                    <Snackbar
                        open={!!uploadSuccess}
                        autoHideDuration={6000}
                        onClose={() => setUploadSuccess(null)}
                        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <Alert
                            onClose={() => setUploadSuccess(null)}
                            severity={
                                uploadSuccess?.includes('Failed')
                                    ? 'error'
                                    : 'success'
                            }
                            sx={{ width: '100%' }}
                        >
                            {uploadSuccess}
                        </Alert>
                    </Snackbar>
                </Paper>
            </Modal>
        </ThemeProvider>
    )
}

export default SourceSelectionModal
